import React from 'react';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { CameraAlt, Description, Download, InsertDriveFile, PlayCircle, Videocam } from '@mui/icons-material';
import { grey, blue } from '@mui/material/colors';
import { getAssetUrl } from 'utils/getAssetUrl';

import { EquipmentAsset } from './EquipmentDetailsTypes';

interface MediaDocument {
  name: string;
  size: string;
  url: string;
}

interface MediaTabProps {
  assets: EquipmentAsset[];
  documents?: MediaDocument[];
  videos?: EquipmentAsset[];
}

export const MediaTab = ({ assets, documents = [], videos = [] }: MediaTabProps) => {
  const images = assets.filter(a => !a.type || a.type === 'IMAGE');

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mt={2}>
      {/* Images */}
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <CameraAlt fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
            Images
          </Typography>
        </Stack>

        {images.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {images.map(image => (
              <Box key={image.id} sx={{ position: 'relative', width: 80, height: 60 }}>
                <Box
                  component="img"
                  src={getAssetUrl(image.source)}
                  alt="equipment"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 0.5,
                  }}
                />
                <IconButton
                  size="small"
                  component="a"
                  href={getAssetUrl(image.source)}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    padding: '2px',
                    backgroundColor: blue[500],
                    color: 'white',
                    '&:hover': { backgroundColor: blue[700] },
                  }}
                >
                  <Download sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No images available.
          </Typography>
        )}
      </Paper>

      {/* Documents */}
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <Description fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
            Documents
          </Typography>
        </Stack>

        {documents.length > 0 ? (
          <Stack gap={1}>
            {documents.map((doc, index) => (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  p: 1,
                  borderRadius: 0.5,
                  backgroundColor: grey[50],
                  border: `1px solid ${grey[200]}`,
                }}
              >
                <IconButton
                  size="small"
                  component="a"
                  href={doc.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    padding: '4px',
                    backgroundColor: blue[500],
                    color: 'white',
                    '&:hover': { backgroundColor: blue[700] },
                  }}
                >
                  <Download sx={{ fontSize: 16 }} />
                </IconButton>
                <InsertDriveFile fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {doc.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {doc.size}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No documents available.
          </Typography>
        )}
      </Paper>

      {/* Videos */}
      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <Videocam fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'none' }}>
            Videos
          </Typography>
        </Stack>

        {videos.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {videos.map(video => (
              <Box key={video.id} sx={{ position: 'relative', width: 100, height: 70 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: grey[800],
                    borderRadius: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayCircle sx={{ fontSize: 32, color: 'white', opacity: 0.8 }} />
                </Box>
                <IconButton
                  size="small"
                  component="a"
                  href={getAssetUrl(video.source)}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    padding: '2px',
                    backgroundColor: blue[500],
                    color: 'white',
                    '&:hover': { backgroundColor: blue[700] },
                  }}
                >
                  <Download sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No videos available.
          </Typography>
        )}
      </Paper>
    </Stack>
  );
};
